import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import PostTruckFormCard, { INITIAL_DATA, type TruckFormData, type ContactInfo, type PostedTruckData, getLastTruckStorageKey, LS_KEY as LEGACY_LS_KEY } from "@/components/post-truck/PostTruckFormCard";
import PostTruckSummaryCard from "@/components/post-truck/PostTruckSummaryCard";
import TruckTemplatesSection, { type TruckTemplate } from "@/components/post-truck/TruckTemplatesSection";
import SaveTemplateModal from "@/components/post-truck/SaveTemplateModal";
import PostTruckMatchesAfterPost from "@/components/post-truck/PostTruckMatchesAfterPost";
import { usePageMeta } from "@/hooks/usePageMeta";
import { DemoHint } from "@/components/demo/DemoHint";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTruckPostMatches } from "@/hooks/useTruckPostMatches";
import { useVerificationWarning } from "@/hooks/useVerificationWarning";
import { VerificationWarningModal } from "@/components/verification/VerificationWarningModal";

function loadSavedForm(userId: string): Partial<TruckFormData> {
  try {
    const raw = localStorage.getItem(getLastTruckStorageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      origin: parsed.origin || "",
      destination: parsed.destination || "",
      openToAnywhere: !!parsed.openToAnywhere,
      equipmentType: parsed.equipmentType || "",
      length: parsed.length || "",
      maxWeight: parsed.maxWeight || "",
      loadType: parsed.loadType || "FTL",
    };
  } catch {
    return {};
  }
}

function saveFormToStorage(userId: string, form: TruckFormData) {
  try {
    const stored = {
      origin: form.origin,
      destination: form.destination,
      openToAnywhere: form.openToAnywhere,
      equipmentType: form.equipmentType,
      length: form.length,
      maxWeight: form.maxWeight,
      loadType: form.loadType,
    };
    localStorage.setItem(getLastTruckStorageKey(userId), JSON.stringify(stored));
  } catch { /* quota or private browsing */ }
}

/** Remove the old browser-wide key to prevent cross-account leakage */
function cleanupLegacyKey() {
  try {
    localStorage.removeItem(LEGACY_LS_KEY);
  } catch { /* ignore */ }
}

export default function PostTruckPageV2() {
  usePageMeta({ title: "Post Truck | LaneRunner", description: "Post your truck capacity" });
  const { showWarningOrRun, modalProps } = useVerificationWarning();
  useEffect(() => { showWarningOrRun(() => {}); }, [showWarningOrRun]);
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const editTruckId = searchParams.get("edit");
  const duplicateTruckId = searchParams.get("duplicate");
  const repostTruckId = searchParams.get("repost");
  const templateId = searchParams.get("templateId");

  const [form, setForm] = useState<TruckFormData>(() => ({ ...INITIAL_DATA, availabilityDate: new Date() }));

  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    name: "",
    companyName: "",
    phone: "",
    email: "",
  });

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TruckTemplate[]>([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [postedTruck, setPostedTruck] = useState<PostedTruckData | null>(null);
  const matchResult = useTruckPostMatches(postedTruck);

  // Load truck data for edit or duplicate
  useEffect(() => {
    const truckId = editTruckId || duplicateTruckId || repostTruckId;
    if (!truckId) return;

    (async () => {
      const { data, error } = await supabase
        .from("truck_postings")
        .select("*")
        .eq("id", truckId)
        .maybeSingle();

      if (error || !data) {
        toast({ title: "Failed to load truck data", variant: "destructive" });
        return;
      }

      setForm({
        origin: data.origin_location || "",
        destination: data.destination_location === "Anywhere" ? "" : data.destination_location || "",
        openToAnywhere: data.destination_location === "Anywhere",
        availabilityDate: (duplicateTruckId || repostTruckId) ? new Date() : new Date(data.available_from),
        equipmentType: data.equipment_type || "",
        length: data.length_ft ? String(data.length_ft) : "",
        maxWeight: data.weight_capacity_lbs ? String(data.weight_capacity_lbs) : "",
        loadType: (data.load_type as TruckFormData["loadType"]) || "",
        notes: data.comments || "",
        visible: data.status !== "private",
      });
    })();
  }, [editTruckId, duplicateTruckId, repostTruckId]);

  // Pre-fill form from a saved truck template (?templateId=...)
  useEffect(() => {
    if (!templateId) return;
    (async () => {
      const { data, error } = await supabase
        .from("truck_templates")
        .select("*")
        .eq("id", templateId)
        .maybeSingle();

      if (error || !data) {
        toast({ title: "Failed to load template", variant: "destructive" });
        return;
      }

      const t = data as TruckTemplate;
      setForm((prev) => ({
        ...prev,
        origin: t.origin || "",
        destination: t.open_to_anywhere ? "" : (t.destination || ""),
        openToAnywhere: !!t.open_to_anywhere,
        equipmentType: t.equipment_type || "",
        length: t.length_ft ? String(t.length_ft) : "",
        maxWeight: t.max_weight_lbs ? String(t.max_weight_lbs) : "",
        loadType: (t.load_type as TruckFormData["loadType"]) || "",
        availabilityDate: new Date(),
      }));

      // Track usage (best-effort)
      supabase
        .from("truck_templates")
        .update({
          usage_count: (t.usage_count || 0) + 1,
          last_used_at: new Date().toISOString(),
        })
        .eq("id", t.id)
        .then(({ error: updErr }) => {
          if (updErr) console.error("Template usage update error:", updErr);
        });

      // Strip templateId from URL so refresh doesn't re-apply
      const next = new URLSearchParams(searchParams);
      next.delete("templateId");
      setSearchParams(next, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Clean up legacy key and load user-scoped saved form once user is available
  useEffect(() => {
    if (!user || editTruckId || duplicateTruckId || repostTruckId) return;
    cleanupLegacyKey();
    const saved = loadSavedForm(user.id);
    if (Object.keys(saved).length > 0) {
      setForm((prev) => ({ ...prev, ...saved }));
    }
  }, [user, editTruckId, duplicateTruckId, repostTruckId]);

  // Compute hasSavedTruck
  const hasSavedTruck = useMemo(() => {
    if (!user) return false;
    try {
      return !!localStorage.getItem(getLastTruckStorageKey(user.id));
    } catch {
      return false;
    }
  }, [user, form]); // re-check when form changes (after a post)

  // Save last truck callback (passed to PostTruckFormCard)
  const handleSaveLastTruck = useCallback(() => {
    if (!user) return;
    saveFormToStorage(user.id, form);
  }, [user, form]);

  // Fetch contact info + company
  useEffect(() => {
    if (!user || !profile) return;

    const name = [profile.first_name, profile.last_name].filter(Boolean).join(" ");
    const email = profile.email || user.email || "";
    setContactInfo((prev) => ({ ...prev, name, email }));

    (async () => {
      try {
        const { data: cu } = await supabase
          .from("company_users")
          .select("company_id")
          .eq("user_id", user.id)
          .eq("is_active", true)
          .maybeSingle();

        if (cu?.company_id) {
          setCompanyId(cu.company_id);
          const { data: company } = await supabase
            .from("companies")
            .select("name, phone")
            .eq("id", cu.company_id)
            .maybeSingle();

          if (company) {
            setContactInfo((prev) => ({
              ...prev,
              companyName: company.name || "",
              phone: company.phone || "",
            }));
          }
        }
      } catch (err) {
        console.error("[PostTruck] Failed to fetch company info:", err);
      }
    })();
  }, [user, profile]);

  // Fetch templates when companyId is known
  const fetchTemplates = useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from("truck_templates")
      .select("*")
      .eq("company_id", companyId)
      .order("usage_count", { ascending: false })
      .order("last_used_at", { ascending: false, nullsFirst: false });
    if (data) setTemplates(data as TruckTemplate[]);
  }, [companyId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Suggest template name from current form
  const suggestTemplateName = () => {
    const parts: string[] = [];
    if (form.origin) parts.push(form.origin.split(",")[0].trim());
    if (form.destination && !form.openToAnywhere) {
      parts.push("→");
      parts.push(form.destination.split(",")[0].trim());
    } else if (form.openToAnywhere) {
      parts.push("→ Anywhere");
    }
    if (form.equipmentType) parts.push(form.equipmentType.replace(/_/g, " "));
    return parts.join(" ") || "New Template";
  };

  const handleSaveTemplate = async (name: string) => {
    if (!companyId || !user) {
      toast({ title: "You must belong to a company to save templates.", variant: "destructive" });
      return;
    }
    const { error } = await supabase.from("truck_templates").insert({
      company_id: companyId,
      created_by: user.id,
      name,
      origin: form.origin,
      destination: form.openToAnywhere ? "" : form.destination,
      equipment_type: form.equipmentType,
      length_ft: form.length ? parseInt(form.length) : 0,
      max_weight_lbs: form.maxWeight ? parseInt(form.maxWeight) : 0,
      load_type: form.loadType,
      open_to_anywhere: form.openToAnywhere,
    });
    if (error) {
      toast({ title: "Failed to save template", description: error.message, variant: "destructive" });
      throw error;
    }
    toast({ title: "Template saved!" });
    fetchTemplates();
  };

  const handleUseTemplate = async (t: TruckTemplate) => {
    setForm((prev) => ({
      ...prev,
      origin: t.origin,
      destination: t.open_to_anywhere ? "" : t.destination,
      openToAnywhere: t.open_to_anywhere,
      equipmentType: t.equipment_type,
      length: t.length_ft ? String(t.length_ft) : "",
      maxWeight: t.max_weight_lbs ? String(t.max_weight_lbs) : "",
      loadType: (t.load_type as TruckFormData["loadType"]) || "",
      availabilityDate: new Date(),
    }));
    // Track usage
    await supabase
      .from("truck_templates")
      .update({ usage_count: t.usage_count + 1, last_used_at: new Date().toISOString() })
      .eq("id", t.id);
    setTemplates((prev) =>
      prev.map((tp) =>
        tp.id === t.id ? { ...tp, usage_count: tp.usage_count + 1, last_used_at: new Date().toISOString() } : tp
      )
    );
  };

  const handleRenameTemplate = async (id: string, newName: string) => {
    await supabase.from("truck_templates").update({ name: newName }).eq("id", id);
    setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, name: newName } : t)));
  };

  const handleDeleteTemplate = async (id: string) => {
    await supabase.from("truck_templates").delete().eq("id", id);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    toast({ title: "Template deleted." });
  };

  const handleDuplicate = () => {
    if (!user) return;
    const saved = loadSavedForm(user.id);
    if (Object.keys(saved).length === 0) return;
    setForm((prev) => ({ ...prev, ...saved, availabilityDate: new Date() }));
  };


  return (
    <>
      <div className="min-h-screen flex flex-col bg-background">
        <div className="px-3 sm:px-4 py-4 space-y-4 w-full min-w-0">
          <DemoHint hintKey="page.postTruck" side="bottom" showIndicator>
            <h1 className="text-xl font-bold text-foreground mb-2">Post Truck</h1>
          </DemoHint>
          <TruckTemplatesSection
            templates={templates}
            onUse={handleUseTemplate}
            onRename={handleRenameTemplate}
            onDelete={handleDeleteTemplate}
          />

          <div className="flex-1 gap-4 items-start grid grid-cols-1 md:[grid-template-columns:1fr_340px] min-w-0 w-full">
            <PostTruckFormCard
              form={form}
              setForm={setForm}
              contactInfo={contactInfo}
              onSaveTemplate={() => setSaveModalOpen(true)}
              onSaveLastTruck={handleSaveLastTruck}
              onPostSuccess={setPostedTruck}
              editTruckId={editTruckId}
              onEditSuccess={() => navigate("/my-trucks")}
            />
            <PostTruckSummaryCard
              form={form}
              contactInfo={contactInfo}
              hasSavedTruck={hasSavedTruck}
              onDuplicate={handleDuplicate}
            />
          </div>
        </div>
      </div>

      <SaveTemplateModal
        open={saveModalOpen}
        onOpenChange={setSaveModalOpen}
        suggestedName={suggestTemplateName()}
        onConfirm={handleSaveTemplate}
      />

      <PostTruckMatchesAfterPost
        open={!!postedTruck}
        onClose={() => setPostedTruck(null)}
        matchResult={matchResult}
      />
      <VerificationWarningModal {...modalProps} />
    </>
  );
}
